import { BaseViewStore, BaasicDropdownStore } from "core/stores";
import { ModalParams } from 'core/models';
import { PatientService } from "application/patient/services";
import { FacilityOrderTemplateService, FacilityOrderTemplateItemService, ItemSubcategoryService } from "application/facility-order/services";
import { FacilityOrderPatientService } from "application/facility-order-new-flow-insurance/services";
import { action, observable, runInAction } from "mobx";
import _ from 'lodash';
import { uuid } from "core/utils";

class FacilityOrderTemplateNewFlowEditViewStore extends BaseViewStore {
	@observable accessor items;
	@observable accessor productStore;
	@observable accessor facilityOrderPatient;

	@observable accessor hospitalClassName = "";
	@observable accessor dischargedClassName = "";
	@observable accessor expiredClassName = "";
	@observable accessor activeClassName = "";

	@observable accessor isDisabledTableInactiveOption = false;
	@observable accessor isDisabledAddTemplate = false;
	@observable accessor showDropdownTableInactiveOptions = false;
	@observable accessor showTemplateNote = false;
	@observable accessor isNoChangeClicked = false;
	@observable accessor activatingInProgress = false;
	@observable accessor isQtyChange = false;

	inactiveOptions = [{ id: "15", name: "Hospital" }, { id: "17", name: "Discharged" }, { id: "18", name: "Expired" }, { id: "0", name: "Active" }];
	discontinuedInactiveOption = { id: "58", name: "Discontinued" };

	constructor(rootStore, componentsProps) {
        super(rootStore);

        this.id = componentsProps.id;
        this.rootStore = rootStore;
        this.patient = componentsProps.item[0].patient;
        this.facilityOrderId = componentsProps.facilityOrder.id;
        this.facilityOrder = componentsProps.facilityOrder;
        this.facilityOrderPatient = componentsProps.facilityOrderPatient;
        this.lookups = componentsProps.lookups;
        this.selectedStatusAbrv = componentsProps.selectedStatusAbrv;
        this.isSelectedItemCategory = componentsProps.selectedItemCategories && componentsProps.selectedItemCategories.length > 0;
        this.checkIsInitialReviewCompleted = componentsProps.checkIsInitialReviewCompleted;
        this.selectedInactiveOption = "";
        this.refresh = componentsProps.refresh;
        this.fetchTemplatesByPatientId = componentsProps.fetchTemplatesByPatientId;
        this.isMasterPatient = this.facilityOrder.facility.masterPatientId == this.patient.patientKey;

        this.patientService = new PatientService(rootStore.application.baasic.apiClient);
        this.facilityOrderTemplateService = new FacilityOrderTemplateService(rootStore.application.baasic.apiClient);
        this.facilityOrderTemplateItemService = new FacilityOrderTemplateItemService(rootStore.application.baasic.apiClient);
        this.itemSubcategoryService = new ItemSubcategoryService(rootStore.application.baasic.apiClient);
        this.facilityOrderPatientService = new FacilityOrderPatientService(rootStore.application.baasic.apiClient);

        this.createFacilityOrderTemplateNewFlowModal = new ModalParams({ onClose: this.selectedStatusAbrv === "new-order" ? () => this.refresh() : null });
        this.facilityOrderTemplateNewFlowPortalNoteModal = new ModalParams({});
        this.facilityOrderTemplateItemNewFlowCalculationModal = new ModalParams({});
        this.facilityOrderTemplateItemNewFlowNoteModal = new ModalParams({});
        this.facilityOrderTemplateItemNewFlowDeleteModal = new ModalParams({});
        this.facilityOrderTemplateItemNewFlowDeleteQuestionModal = new ModalParams({});
        this.facilityOrderTemplateNewFlowDeleteModal = new ModalParams({});
        this.facilityOrderTemplateItemNewFlowActivatePatientModal = new ModalParams({});

        this.insertImportActionId = this.lookups.importAction.find((s) => s.abrv === 'insert').id;

        this.fetch([
			this.initialize(componentsProps.item)
		]);
    }

	@action.bound
	async initialize(items) {
		await this.initializePatient();
		_.forEach(items, (facilityOrderTemplate) => {
			_.forEach(facilityOrderTemplate.facilityOrderTemplateItems, (facilityOrderTemplateItem) => {
				facilityOrderTemplateItem.lastTemplateQuantity = facilityOrderTemplateItem.templateQuantity;
				facilityOrderTemplateItem.lastOrderQuantity = facilityOrderTemplateItem.orderQuantity;
				facilityOrderTemplateItem.lastNote = facilityOrderTemplateItem.note;
			});
		});
		this.items = items;
		this.getPatientStatusButtonClassName();
		this.isTemplateActionAllowed();
	}

	async fetchTemplates() {
		let items = await this.fetchTemplatesByPatientId();
		_.forEach(items, (facilityOrderTemplate) => {
			_.forEach(facilityOrderTemplate.facilityOrderTemplateItems, (facilityOrderTemplateItem) => {
				facilityOrderTemplateItem.lastTemplateQuantity = facilityOrderTemplateItem.templateQuantity;
				facilityOrderTemplateItem.lastOrderQuantity = facilityOrderTemplateItem.orderQuantity;
				facilityOrderTemplateItem.lastNote = facilityOrderTemplateItem.note;
			});
		});
		this.items = items;
	}

	@action.bound
	async setInactiveOption(
        newFlowStatusAbrv,
        inactiveOptionId,
        templateId,
        setInactiveReasonAndStatuses = false
    ) {
		if (templateId != null) {
			this.items.find(item => item.id === templateId).inactiveReasonId = inactiveOptionId;
		} else {
			_.each(this.items, item => {
				item.inactiveReasonId = inactiveOptionId;
				item.inactiveReason = this.inactiveOptions.find(i => i.id === inactiveOptionId).name;
			});
			this.getPatientStatusButtonClassName(inactiveOptionId);
			this.isTemplateActionAllowed();
		}
		await this.setNewFlowStatus(newFlowStatusAbrv, templateId, inactiveOptionId, setInactiveReasonAndStatuses);
	}

	@action.bound
	async setNewFlowStatus(
        statusAbrv,
        templateId,
        inactiveOptionId,
        setInactiveReasonAndStatuses = false
    ) {
		try {
			this.isNoChangeClicked = true;
			const status = this.lookups.newFlowStatus.find(s => s.abrv === statusAbrv);
			let updateItems = this.items.filter(item => !templateId || item.id === templateId);

			if (this.isSelectedItemCategory && !templateId && statusAbrv === "discharge") {
				const selectedStatus = this.lookups.newFlowStatus.find(s => s.abrv === this.selectedStatusAbrv);
				updateItems = (await this.facilityOrderTemplateService.find({
					facilityOrderId: this.facilityOrderId,
					patientId: this.id,
					newFlowStatusId: selectedStatus.id,
				})).data.item;
			}

			await this.facilityOrderTemplateService.batchUpdate(_.map(updateItems, item => {
				return {
					id: item.id,
					inactiveReasonId: inactiveOptionId ? inactiveOptionId : item.inactiveReasonId,
					newFlowStatusId: status.id,
					newFlowInsuranceStatusId: statusAbrv === "discharge" || statusAbrv === "in-progress" ? status.id : (this.lookups.newFlowStatus.find(s => s.abrv === "in-progress")).id,
					portalNote: statusAbrv === "no-change" || statusAbrv === "in-progress" ? null : item.portalNote,
					newFlowStatusUpdatedByUserId: this.rootStore.userStore.user.id,
					newFlowUSIntakeStatusId: status.id,
					setInactiveReason: setInactiveReasonAndStatuses,
					isTemplateStatus: templateId ? true : false,
					setStatuses: setInactiveReasonAndStatuses && !templateId ? true : false,
					setInsuranceStatus: this.selectedStatusAbrv === "discharge" && statusAbrv !== "discharge" && templateId ? true : false
				};
			}));

			if (statusAbrv !== this.selectedStatusAbrv) {
				this.items = (templateId && this.items.length > 1) ? this.items.filter(s => s.id !== templateId) : null;

				if (!this.items && this.selectedStatusAbrv === 'in-progress') {
					await this.checkIsInitialReviewCompleted();
				}
			}
			this.isNoChangeClicked = false;
		}
		catch (err) {
			this.rootStore.notificationStore.error(
				"An unexpected error occurred while trying to update the template.",
				err
			);
		}
	}

	@action.bound
	async initializePatient() {
		this.isNewPatient = (await this.facilityOrderTemplateService.isNewTemplate(this.facilityOrder.id, this.id)).data;
	}

	@action.bound
	openCreateFacilityOrderTemplateNewFlowModal() {
		this.createFacilityOrderTemplateNewFlowModal.open({
			facilityOrderId: this.facilityOrderId,
			patientId: this.id,
			patientKey: this.patient.patientKey,
			patient: this.patient,
			facilityOrder: this.facilityOrder
		});
	}

	@action.bound
	async openFacilityOrderTemplateNewFlowPortalNoteModal(
        facilityOrderTemplateId,
        statusAbrv,
        isEdit = false,
        isBatchUpdate = false,
        inactiveOptionId = null,
        setInactiveReasonAndStatuses = false,
        showWarningMessage = false
    ) {
		if (this.selectedStatusAbrv != statusAbrv || isEdit) {
			this.facilityOrderTemplateNewFlowPortalNoteModal.open({
				title: statusAbrv === "discharge" ? "Inactive Order Note" : statusAbrv === "new-order" ? "New Order Note" : "Reason for Change / Review",
				facilityOrderTemplateId: facilityOrderTemplateId ? facilityOrderTemplateId : this.items[0].id,
				isBatchUpdate: isBatchUpdate,
				setNewFlowStatus: isBatchUpdate ? () => this.setInactiveOption("discharge", inactiveOptionId, null, setInactiveReasonAndStatuses) : isEdit ? () => { } : statusAbrv === "discharge" ? () => this.setInactiveOption(statusAbrv, this.discontinuedInactiveOption.id, facilityOrderTemplateId, setInactiveReasonAndStatuses) : () => this.setNewFlowStatus(statusAbrv, facilityOrderTemplateId, inactiveOptionId, setInactiveReasonAndStatuses),
				refreshPortalNote: (portalNote) => this.refreshPortalNote(facilityOrderTemplateId, portalNote),
				setPatientStatus: async () => await this.setPatientStatus(inactiveOptionId == "15" ? "Hospital" : inactiveOptionId == "17" ? "Discharged" : inactiveOptionId == "18" ? "Expired" : "Active"),
				showWarningMessage: showWarningMessage
			});
		}
	}

	@action.bound
	isTemplateActionAllowed() {
		this.isDisabledTableInactiveOption = false;
		this.isDisabledAddTemplate = false;
		this.showDropdownTableInactiveOptions = false;
		this.showTemplateNote = false;

		if (this.selectedStatusAbrv === "in-progress" && this.isHospitalStatus()) {
			this.isDisabledTableInactiveOption = true;
		}
		else if (this.selectedStatusAbrv === "new-order") {
			this.showTemplateNote = true;
		}
		else if (this.selectedStatusAbrv === "new-order" && this.isHospitalStatus()) {
			this.isDisabledTableInactiveOption = true;
			this.showTemplateNote = true;
		}
		else if (this.selectedStatusAbrv === "change") {
			this.showDropdownTableInactiveOptions = true;
			this.showTemplateNote = true;
		}
		else if (this.selectedStatusAbrv === "no-change") {
			this.showDropdownTableInactiveOptions = true;
		}
		else if (this.selectedStatusAbrv === "discharge" && this.items) {
			this.showTemplateNote = true;
			if (this.items.every(i => i.inactiveReason === 'Hospital') || this.items.every(i => i.inactiveReason === 'Discharged') || this.items.every(i => i.inactiveReason === 'Expired')) {
				this.isDisabledTableInactiveOption = true;
				this.isDisabledAddTemplate = true;
			}
			else {
				this.showDropdownTableInactiveOptions = true;
			}
		}
	}

	@action.bound
	isHospitalStatus() {
		if (this.items.every(i => i.inactiveReason === 'Hospital')) {
			return true;
		}

		var templates = _.orderBy(this.items, [item => (item.templateId === null ? 0 : item.templateId)], ['desc']);
		const gTubeSubCategory = this.lookups.subCategory.find(x => x.abrv == 'G-Tube');
		const trachTubSubCategory = this.lookups.subCategory.find(x => x.abrv == 'Trach-Tube');
		const noSubCategory = this.lookups.subCategory.find(x => x.abrv == 'No-Sub-Category');
		const enteralCategory = this.lookups.itemCategory.find(x => x.abrv == 'enteral');

		var template = templates.find(t => t.itemCategoryId == enteralCategory.id && t.subCategoryId != gTubeSubCategory.id);
		if (template) {
			return template.inactiveReason === 'Hospital';
		}

		template = templates.find(t => t.subCategoryId != gTubeSubCategory.id && t.subCategoryId != trachTubSubCategory.id && t.subCategoryId != noSubCategory.id);
		if (template) {
			return template.inactiveReason === 'Hospital';
		}

		return templates[0] && templates[0].inactiveReason === 'Hospital';
	}

	@action.bound
	getPatientStatusButtonClassName(inactiveOptionId = null) {
		this.hospitalClassName = "";
		this.dischargedClassName = "";
		this.expiredClassName = "";
		this.activeClassName = "";

		this.inactiveOptions.filter(i => !inactiveOptionId || i.id === inactiveOptionId).forEach((item) => {
			if (this.selectedStatusAbrv === "in-progress") {
				if (item.name === "Hospital" && this.facilityOrderPatient.activeStatus === 'Hospital') {
					this.hospitalClassName = "is-active--alt"
				}
				else if (item.name === "Active" && this.facilityOrderPatient.activeStatus !== 'Hospital') {
					this.activeClassName = "is-active";
				}
			}
			else if (this.selectedStatusAbrv === "new-order" || this.selectedStatusAbrv === "change" || this.selectedStatusAbrv === "no-change") {
				if (item.name === "Active") {
					this.activeClassName = "is-active";
				}
			}
			else if (this.selectedStatusAbrv === "discharge") {
				if (item.name === "Hospital" && this.facilityOrderPatient.activeStatus === 'Hospital') {
					this.hospitalClassName = "is-active";
				}
				else if (item.name === "Discharged" && this.facilityOrderPatient.activeStatus === 'Discharged') {
					this.dischargedClassName = "is-active";
				}
				else if (item.name === "Expired" && this.facilityOrderPatient.activeStatus === 'Expired') {
					this.expiredClassName = "is-active";
				}
				else if (item.name === "Active" && this.facilityOrderPatient.activeStatus !== 'Hospital' && this.facilityOrderPatient.activeStatus !== 'Discharged' && this.facilityOrderPatient.activeStatus !== 'Expired') {
					this.activeClassName = "is-active";
				}
			}
		});
	}

	@action.bound
	async setProductStore(subCategoryId) {
		const ostomySubCategory = this.lookups.subCategory.find(x => x.abrv == 'Ostomy-Products');
		const isOstomySubCategory = subCategoryId === ostomySubCategory.id;

		this.productStore = new BaasicDropdownStore({
			filterable: true,
			textField: "itemId",
		}, {
			fetchFunc: async (term) => {
				if ((term && term.trim()) === '')
					term = undefined;

				let params = {
					searchQuery: term,
					rpp: 50,
					subCategoryId: subCategoryId,
					isActive: true,
					embed: "Product,Product.ItemType,Product.ItemCategory,Product.SubCategory,Product.UnitOfMeasure,Product.ProcedureCode_"
				}

				if (isOstomySubCategory) {
					params = { ...params, sort: "isPreferred|desc" };
				}

				const response = await this.itemSubcategoryService.find(params);
				let filteredProducts = this.isMasterPatient ? response.data.item : response.data.item.filter(x => !x.product.itemId.toLowerCase().includes("-cs"));

				return _.map(filteredProducts, item => {
					return {
						...item.product
					};
				});
			}
		});
	}

	@action.bound
	async addEmptyItem(facilityOrderTemplateId) {
        let facilityOrderTemplate = this.items.find(item => item.id === facilityOrderTemplateId);
		if(facilityOrderTemplate.facilityOrderTemplateItems && facilityOrderTemplate.facilityOrderTemplateItems.some(i => !i.productId)){
			return;
		}

		await this.setProductStore(facilityOrderTemplate.subCategoryId);

        facilityOrderTemplate.facilityOrderTemplateItems.push({
            id: uuid(),
            note: null,
            product: null,
            productId: null,
            procedureCode: null,
            mlph: undefined,
            frequency: undefined,
            itemCals: 0,
            itemMls: 0,
            templateQuantity: null,
            orderQuantity: 0,
            importActionId: this.lookups.importAction.find(x => x.abrv === 'insert').id,
            importDetailStatusId: this.lookups.importDetailStatus.find(x => x.abrv === 'new').id,
            facilityOrderTemplateId: facilityOrderTemplateId,
			toggle: true
        });

    }

	@action.bound
	setItemToggle(facilityOrderTemplateItem) {
		facilityOrderTemplateItem.toggle = false;
	}
	
	@action.bound
	editItem(facilityOrderTemplateItem, value, propertyName) {
		facilityOrderTemplateItem[propertyName] = value;
		if (propertyName === "templateQuantity") {
			facilityOrderTemplateItem["orderQuantity"] = value
		}
	}

	@action.bound
	async saveItem(
        facilityOrderTemplateItem,
        facilityOrderTemplateItems,
        index,
        product,
        facilityOrderTemplate
    ) {
		if (product) {
			let tempItem = {
				product: product,
				productId: product.id,
				procedureCode: product.procedureCode_ && product.procedureCode_.abrv,
				importActionId: facilityOrderTemplateItem.importActionId,
				importDetailStatusId: facilityOrderTemplateItem.importDetailStatusId,
				facilityOrderTemplateId: facilityOrderTemplate.id,
			};

			const isEnteral = this.lookups.itemCategory.some(i => i.id === facilityOrderTemplate.itemCategoryId && i.abrv.includes("enteral"));
			const itemType = (product.itemType && (product.itemType.abrv === 'bottle' || product.itemType.abrv === 'can')) || (product.procedureCode_ && product.procedureCode_.abrv === 'B4155')
			if (!this.isMasterPatient && isEnteral && itemType) {
				_.extend(tempItem, { itemMls: 1, itemCals: product.calsPerMl });
			}

			try {
				const response = await this.facilityOrderTemplateItemService.createAdditionalItemsNewFlow(
					{
						facilityOrderId: this.facilityOrderId,
						facilityOrderItem: tempItem,
						facilityOrderItems: facilityOrderTemplateItems.filter(i => i.product).map(i => { return { product: i.product, productId: i.productId, procedureCode: i.procedureCode } }),
						subCategoryId: facilityOrderTemplate.subCategoryId,
						clinicalStatusId: facilityOrderTemplate.clinicalStatusId,
						isMasterPatient: this.isMasterPatient
					});

				if (tempItem.product.innerCannulaId && facilityOrderTemplate.linkedTemplateId == null) {
					this.refresh();
					return;
				}

				this.addItem(facilityOrderTemplateItems, response, index);

				if (!this.isMasterPatient && isEnteral && itemType) {
					index = facilityOrderTemplateItems.indexOf(facilityOrderTemplateItems.slice(index).find(i => i.productId == tempItem.productId));
					this.openFacilityOrderTemplateItemNewFlowCalculationModal(facilityOrderTemplateItems, index);
				}
			}
			catch (err) {
				return this.rootStore.notificationStore.error("FACILITY_ORDER_TEMPLATE_ITEM.EDIT.ERROR");
			}
		}
	}

	@action.bound
	async addItem(facilityOrderTemplateItems, items, index) {
		facilityOrderTemplateItems.splice(index);
		_.each(items, i => {
			facilityOrderTemplateItems.push({
				id: i.id,
				note: i.note,
				product: i.product,
				productId: i.product.id,
				procedureCode: i.procedureCode,
				mlph: i.mlph,
				frequency: i.frequency,
				itemCals: i.itemCals,
				itemMls: i.itemMls,
				templateQuantity: i.templateQuantity,
				orderQuantity: 0,
				importActionId: i.importActionId,
				importDetailStatusId: i.importDetailStatusId,
				facilityOrderTemplateId: i.facilityOrderTemplateId,
			});
		});
	}

	@action.bound
	async updateItem(facilityOrderTemplateItem, facilityOrderTemplate, isNote) {

		if (Number.isNaN(facilityOrderTemplateItem.templateQuantity)  || facilityOrderTemplateItem.templateQuantity === '' || facilityOrderTemplateItem.templateQuantity === null || facilityOrderTemplateItem.templateQuantity === undefined) {
            facilityOrderTemplateItem.templateQuantity = 0;
        }
		
        if (Number.isNaN(facilityOrderTemplateItem.orderQuantity) ||facilityOrderTemplateItem.orderQuantity === '' || facilityOrderTemplateItem.orderQuantity === null || facilityOrderTemplateItem.orderQuantity === undefined) {
            facilityOrderTemplateItem.orderQuantity = 0;
        }

		if (facilityOrderTemplateItem.templateQuantity == facilityOrderTemplateItem.lastTemplateQuantity &&
            facilityOrderTemplateItem.orderQuantity == facilityOrderTemplateItem.lastOrderQuantity &&
            facilityOrderTemplateItem.note == facilityOrderTemplateItem.lastNote) {
            return;
        }

        const importActionDelete = this.lookups.importAction.find(x => x.abrv === "delete");
        if (facilityOrderTemplateItem.templateItemId && facilityOrderTemplateItem.importActionId != importActionDelete.id) {
            facilityOrderTemplateItem.importAction = this.lookups.importAction.find(x => x.abrv === "update");
            facilityOrderTemplateItem.importActionId = facilityOrderTemplateItem.importAction.id;
        }

        if (isNote) {
            await this.facilityOrderTemplateItemService.update(facilityOrderTemplateItem);
            if (facilityOrderTemplateItem.facilityOrderTemplateItemInitial && facilityOrderTemplateItem.note != facilityOrderTemplateItem.facilityOrderTemplateItemInitial.note)
			{
				if(!facilityOrderTemplateItem.isNoteChanged){
					facilityOrderTemplateItem.isNoteChanged = true;
					this.isQtyChange = true;
					this.isQtyChange = false;
				}
			}
            else
                facilityOrderTemplateItem.isNoteChanged = false;
			facilityOrderTemplateItem.lastNote = facilityOrderTemplateItem.note;
        }
        else {
            let result = await this.facilityOrderTemplateItemService.newUpdate(facilityOrderTemplateItem);
            if (result) {
                facilityOrderTemplateItem.orderQuantity = result.orderQuantity;
                facilityOrderTemplateItem.templateQuantity = result.templateQuantity;
                facilityOrderTemplateItem.importActionId = result.importActionId;

				facilityOrderTemplateItem.lastTemplateQuantity = facilityOrderTemplateItem.templateQuantity;
                facilityOrderTemplateItem.lastOrderQuantity = facilityOrderTemplateItem.orderQuantity;
                facilityOrderTemplateItem.lastNote = facilityOrderTemplateItem.note;

                if (facilityOrderTemplateItem.importActionId) {
                    const importAction = this.lookups.importAction.find(x => x.id === facilityOrderTemplateItem.importActionId);
                    facilityOrderTemplateItem.importAction = importAction;
                }
				
                facilityOrderTemplate.isDifferentQty = result.isDifferentQty;

                if (facilityOrderTemplateItem.facilityOrderTemplateItemInitial && result.templateQuantity != facilityOrderTemplateItem.facilityOrderTemplateItemInitial.templateQuantity)
                {
					if(!facilityOrderTemplateItem.isTemplateQuantityChanged){
						facilityOrderTemplateItem.isTemplateQuantityChanged = true;
						this.isQtyChange = true;
						this.isQtyChange = false;
					}
				}    
                else
                    facilityOrderTemplateItem.isTemplateQuantityChanged = false;
            }
        }
    }

	@action.bound
	async deleteItem(facilityOrderTemplateItems, index, facilityOrderTemplateId, deleteTemplate) {
		const isNewProduct = facilityOrderTemplateItems[index].product != null;
		if (facilityOrderTemplateItems[index].product != null) {
			const template = this.items.find(i => i.id === facilityOrderTemplateId);
			const isInnerCannula = facilityOrderTemplateItems[index].product.innerCannulaId != null;

			try {
				await this.facilityOrderTemplateItemService.newDelete(facilityOrderTemplateItems[index]);
			} catch (ex) {
				if (ex && ex.data.includes("FK_InventoryCalculationCutback_FacilityOrderTemplateItem")) {
					this.rootStore.notificationStore.error("Template item cannot be deleted because it is included in the Inventory Calculation.");
				}
				else {
					this.rootStore.notificationStore.error("An unexpected error occurred while trying to delete the item.");
				}
				return;
			}

			if (template.linkedTemplateId && isInnerCannula) {
				this.items.splice(this.items.indexOf(this.items.find((i) => i.id === template.linkedTemplateId)), 1)
				template.linkedTemplateId = null;
			}
		}
		facilityOrderTemplateItems.splice(index, 1);
		if (deleteTemplate && facilityOrderTemplateItems.length === 0) {
			await this.deleteTemplate(facilityOrderTemplateId);
		}

		if (isNewProduct && this.items.find(i => i.id === facilityOrderTemplateId)) {
            this.setDifferentQty(this.items.find(i => i.id === facilityOrderTemplateId), "IsDifferentQty");
        }
	}

	@action.bound
	async deleteTemplate(facilityOrderTemplateId) {
		try {
			const template = this.items.find(i => i.id === facilityOrderTemplateId);

			await this.facilityOrderTemplateService.deleteNewTemplate(facilityOrderTemplateId);
			this.items.splice(this.items.indexOf(this.items.find(i => i.id === facilityOrderTemplateId)), 1);

			if (template.linkedTemplateId) {
				this.items.splice(this.items.indexOf(this.items.find((i) => i.id === template.linkedTemplateId)), 1)
			}
		}
		catch (err) {
			this.rootStore.notificationStore.error("Error occurred while trying to delete a template.");
		}
	}

	@action.bound
	async openFacilityOrderTemplateItemNewFlowActivatePatientModal(
        inactiveOptionId,
        setInactiveReasonAndStatuses = false,
        fetchTemplates = false
    ) {
		if (inactiveOptionId === "0") {
			if (this.selectedStatusAbrv === "discharge") {
				this.facilityOrderTemplateItemNewFlowActivatePatientModal.open({
					patient: this.patient,
					setInactiveOption: () => this.setInactiveOption("in-progress", inactiveOptionId, null, setInactiveReasonAndStatuses),
					setPatientStatus: async () => await this.setPatientStatus("Active")
				});
			}
			else if (this.selectedStatusAbrv === "in-progress") {
				this.activatingInProgress = true;
				await this.setPatientStatus("Active")

				await this.setInactiveOption("in-progress", inactiveOptionId, null, setInactiveReasonAndStatuses);
				if (fetchTemplates)
					await this.fetchTemplates();
				this.activatingInProgress = false;
			}
		}
		else {
			if (this.selectedStatusAbrv !== "discharge") {
				if (!(this.isHospitalStatus() && inactiveOptionId === "15" && this.selectedStatusAbrv === "in-progress")) {
					await this.openFacilityOrderTemplateNewFlowPortalNoteModal(null, "discharge", false, true, inactiveOptionId, setInactiveReasonAndStatuses);
					return;
				}
			}
			await this.setPatientStatus(inactiveOptionId === "15" ? "Hospital" : inactiveOptionId === "17" ? "Discharged" : inactiveOptionId === "18" ? "Expired" : "Active");
			await this.setInactiveOption("discharge", inactiveOptionId, null, setInactiveReasonAndStatuses);
		}
	}

	@action.bound
	async deleteFacilityOrderTemplateItem(facilityOrderTemplateItems, index, facilityOrderTemplateId) {
        if (facilityOrderTemplateItems[index].templateItemId) {
            await this.setImportAction(facilityOrderTemplateItems[index], 'delete');
        }
        else {
            if (this.isNewPatient && this.items.length == 1 && facilityOrderTemplateItems.length == 1) {
                let params = { facilityOrderId: this.facilityOrder.id, patientId: this.patient.id, rpp: 2, fields: "id" };
                if (this.facilityOrder.isEnteralWeek) {
                    params = _.extend(params, {
                        itemCategoryIds: [this.lookups.itemCategory.find(x => x.abrv == 'enteral').id]
                    });
                }
                var response = (await this.facilityOrderTemplateService.find(params)).data.item;
                if (response.length == 1) {
                    this.facilityOrderTemplateItemNewFlowDeleteQuestionModal.open({
                        deleteItem: () => this.deleteItem(facilityOrderTemplateItems, index, facilityOrderTemplateId, false),
                        deletePatient: () => this.deleteItem(facilityOrderTemplateItems, index, facilityOrderTemplateId, true)
                    });
                    return;
                }
            }
            await this.deleteItem(facilityOrderTemplateItems, index, facilityOrderTemplateId, true);
        }
    }

	@action.bound
	async openFacilityOrderTemplateItemNewFlowDeleteModal(facilityOrderTemplateItems, index, facilityOrderTemplateId) {
		if (facilityOrderTemplateItems[index].product) {
			this.facilityOrderTemplateItemNewFlowDeleteModal.open({
				item: facilityOrderTemplateItems[index],
				deleteItem: () => this.deleteItem(facilityOrderTemplateItems, index, facilityOrderTemplateId, true)
			});
		}
		else {
			await this.deleteItem(facilityOrderTemplateItems, index, facilityOrderTemplateId, true);
		}
	}

	@action.bound
	async openFacilityOrderTemplateNewFlowDeleteModal(facilityOrderTemplateId) {
		this.facilityOrderTemplateNewFlowDeleteModal.open({
			item: this.items.find(i => i.id === facilityOrderTemplateId),
			deleteTemplate: () => this.deleteTemplate(facilityOrderTemplateId),
			isNewPatient: this.isNewPatient
		});
	}

	@action.bound
	openFacilityOrderTemplateItemNewFlowCalculationModal(facilityOrderTemplateItems, index) {
		let facilityOrderTemplate = this.items.find(item => item.id === facilityOrderTemplateItems[index].facilityOrderTemplateId);

		this.facilityOrderTemplateItemNewFlowCalculationModal.open({
			facilityOrderTemplateItem: facilityOrderTemplateItems[index],
			facilityOrderId: this.facilityOrderId,
			isUpdate: true,
			lookups: this.lookups,
			facilityOrderTemplate: facilityOrderTemplate
		});
	}

	@action.bound
	openFacilityOrderTemplateItemNewFlowNoteModal(facilityOrderTemplateItems, index) {
		this.facilityOrderTemplateItemNewFlowNoteModal.open({
			facilityOrderTemplateItem: facilityOrderTemplateItems[index],
			isUpdate: true
		});
	}

	@action.bound
	getSelectedInactiveOption() {
		if (this.selectedStatusAbrv === "change") {
			return "warning-triangle";
		}
		else if (this.selectedStatusAbrv === "no-change") {
			return "success";
		}
		else if (this.selectedStatusAbrv === "discharge" && this.items) {
			return "danger";
		}
		return "";
	}

	@action.bound
	async setSelectedInactiveOption(
        abrv,
        facilityOrderTemplateId,
        inactiveReasonId = null,
        setInactiveReasonAndStatuses = false
    ) {
		if (abrv == "no-change") {
			await this.setNewFlowStatus("no-change", facilityOrderTemplateId, inactiveReasonId, setInactiveReasonAndStatuses);
		} else if (abrv == "change") {
			this.openFacilityOrderTemplateNewFlowPortalNoteModal(facilityOrderTemplateId, "change", false, false, inactiveReasonId, setInactiveReasonAndStatuses);
		}
		else if (abrv == "discharge") {
			this.openFacilityOrderTemplateNewFlowPortalNoteModal(facilityOrderTemplateId, "discharge", false, false, null, true)
		}
	}

	@action.bound
	refreshPortalNote(facilityOrderTemplateId, portalNote) {
		this.items.filter(x => facilityOrderTemplateId === null || x.id === facilityOrderTemplateId).map(i => _.extend(i, { portalNote: portalNote }));
	}

	@action.bound
	async setPatientStatus(status) {
		this.facilityOrderPatient.activeStatus = status;
		await this.facilityOrderPatientService.update(this.facilityOrderPatient);
	}

	@action.bound
	async setImportActionToDeleteAndAddItem(item, facilityOrderTemplateItems, index, facilityOrderTemplateId) {
        try {
            if (item.importAction && item.importAction.name === "Delete") {
                await this.setImportAction(item, "update")
            }
            else {
                await this.deleteFacilityOrderTemplateItem(facilityOrderTemplateItems, index, facilityOrderTemplateId)
                this.addEmptyItem(item.facilityOrderTemplateId);
            }
        }
        catch (err) {
            this.rootStore.notificationStore.error("FACILITY_ORDER_TEMPLATE_ITEM.EDIT.DELETE_ERROR");
        }
    }

    @action.bound
    async setImportAction(facilityOrderTemplateItem, action) {
        facilityOrderTemplateItem.importAction = this.lookups.importAction.find(x => x.abrv === action);
        facilityOrderTemplateItem.importActionId = facilityOrderTemplateItem.importAction.id;
        await this.facilityOrderTemplateItemService.update(facilityOrderTemplateItem);

        if (this.items.find(i => i.id === facilityOrderTemplateItem.facilityOrderTemplateId)) {
            this.setDifferentQty(this.items.find(i => i.id === facilityOrderTemplateItem.facilityOrderTemplateId), "IsDifferentQty");
        }
    }

	@action.bound
	async setDifferentQty(facilityOrderTemplate, embed) {
        const response = await this.facilityOrderTemplateService.getFacilityOrderTemplateValidation(facilityOrderTemplate.id, { embed: embed });
        runInAction(() => {
            if (response && response.data && embed) {
                if (embed.includes('IsDoNotSupply')) {
                    facilityOrderTemplate.isDoNotSupply = response.data.isDoNotSupply;
                }
                if (embed.includes('IsDifferentQty')) {
                    facilityOrderTemplate.isDifferentQty = response.data.isDifferentQty;
                }
            }
        });
    }
}

export default FacilityOrderTemplateNewFlowEditViewStore;